import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useConversationUuid } from '~/hooks/conversation/useConversationUuid'
import { useTaskDetails } from '~/hooks/task/useTaskDetails'
import { useTaskProgressMessages } from '~/hooks/task/useTaskProgressMessages'
import { ModalId, useModalController } from '~/hooks/useModal'
import { AgentTaskStatus, Diff } from '~/protos/agents'
import { CollapsibleLogCard, extractProgressMessagesFromDB, FileDropdown } from '../common'

interface Artifact2CodeSlideProps {
  taskStatus?: AgentTaskStatus
}

// Helper function to get diffs from task status, checking multiple states for streaming support
function getDiffsFromTaskStatuses(taskDetails: any, targetStatuses: AgentTaskStatus[]): any[] {
  if (!taskDetails?.task?.details?.orderedStateResponses) {
    return []
  }

  const tuples = taskDetails.task.details.orderedStateResponses

  // Check each target status in priority order
  for (const targetStatus of targetStatuses) {
    // Find the most recent occurrence of this status
    for (let i = tuples.length - 1; i >= 0; i--) {
      const tuple = tuples[i]
      if (tuple.taskStatus === targetStatus) {
        const diffs = tuple.response?.[0]?.responseContentStream?.diff || []
        if (diffs.length > 0) {
          return diffs
        }
        break
      }
    }
  }

  return []
}

export function Artifact2CodeSlide({ taskStatus }: Artifact2CodeSlideProps) {
  const [searchParams] = useSearchParams()
  const conversationUuid = useConversationUuid()
  const { openModal } = useModalController(ModalId.RefactorFileViewer)

  const taskId = searchParams.get('task_id') ?? ''
  const { data: taskDetails } = useTaskDetails(conversationUuid, taskId)

  // Get diffs directly from task details for specific states
  // Check completed state first, then streaming states for incremental updates
  const documentationDiffs = useMemo(
    () =>
      getDiffsFromTaskStatuses(taskDetails, [
        AgentTaskStatus.REFACTOR_DOCS_GENERATED, // Completed state
        AgentTaskStatus.REFACTOR_DOCS_GENERATING, // Streaming state
        AgentTaskStatus.REFACTOR_API_FEEDBACK_PROVIDED // After proceeding from Artifact1
      ]),
    [taskDetails]
  )

  const codeDiffs = useMemo(
    () =>
      getDiffsFromTaskStatuses(taskDetails, [
        AgentTaskStatus.REFACTOR_CODE_GENERATED, // Completed state
        AgentTaskStatus.REFACTOR_API_SPECS_GENERATED // Streaming code generation state
      ]),
    [taskDetails]
  )

  // Simple state-based rendering logic
  // Include REFACTOR_API_PROPOSED to handle the transition period when slide navigates here
  // but WebSocket hasn't updated the status yet
  const isLoading =
    taskStatus === AgentTaskStatus.REFACTOR_API_PROPOSED ||
    taskStatus === AgentTaskStatus.REFACTOR_API_FEEDBACK_PROVIDED ||
    taskStatus === AgentTaskStatus.REFACTOR_DOCS_GENERATING

  // Fetch progress messages from the database - uses WebSocket for real-time updates
  const { data: dbProgressMessages } = useTaskProgressMessages(conversationUuid, taskId, {
    enabled: !!conversationUuid && !!taskId
  })

  // Extract progress messages for the current state using the new DB-backed messages
  const progressMessages = useMemo(
    () => extractProgressMessagesFromDB(dbProgressMessages, taskStatus),
    [dbProgressMessages, taskStatus]
  )

  const isGeneratingCode =
    taskStatus === AgentTaskStatus.REFACTOR_DOCS_GENERATED ||
    taskStatus === AgentTaskStatus.REFACTOR_API_SPECS_GENERATED

  // Determine heading based on state
  const getHeading = () => {
    if (
      taskStatus === AgentTaskStatus.REFACTOR_API_PROPOSED ||
      taskStatus === AgentTaskStatus.REFACTOR_API_FEEDBACK_PROVIDED ||
      taskStatus === AgentTaskStatus.REFACTOR_DOCS_GENERATING
    ) {
      return 'Generating detailed documentation'
    } else if (
      taskStatus === AgentTaskStatus.REFACTOR_DOCS_GENERATED ||
      taskStatus === AgentTaskStatus.REFACTOR_API_SPECS_GENERATED
    ) {
      return 'Generating code'
    } else if (taskStatus === AgentTaskStatus.REFACTOR_CODE_GENERATED) {
      return 'Refactoring completed'
    }
    return 'Refactoring completed'
  }

  // ``FileDropdown.onFileClick`` takes an options object — see
  // ``FileClickArgs`` in ``common/FileComponents.tsx``. The old
  // positional ``(diffs, activePath)`` shape now fails to type-check
  // (defeats TS function-parameter bivariance).
  const handleViewFiles = ({ diffs, activePath }: { diffs: Diff[]; activePath: string }) => {
    if (diffs.length > 0) {
      openModal({ diffs, initialActivePath: activePath })
    }
  }

  return (
    <div className='flex flex-col h-full w-full items-center relative bg-[#fbfbfb] overflow-y-auto'>
      {/* Main Content */}
      <div className='flex flex-col items-center w-full px-[203px] py-[60px]'>
        {/* Heading */}
        <div className='flex flex-col gap-[16px] items-center w-full max-w-[800px] mb-[32px]'>
          <p className="font-['Geist'] font-bold text-[20px] leading-[22px] text-black">
            {getHeading()}
          </p>
          <p className="font-['Geist'] font-medium text-[14px] leading-[22px] text-[rgba(0,0,0,0.65)] text-center">
            {isLoading
              ? 'Creating comprehensive documentation for the refactored architecture'
              : isGeneratingCode
                ? 'Generating code based on the documentation'
                : 'Review the generated documentation and code'}
          </p>
        </div>

        {/* Cards Container */}
        <div className='flex flex-col gap-[24px] items-center w-full max-w-[800px] pb-[60px]'>
          {/* Agent Progress Logs */}
          <CollapsibleLogCard
            messages={progressMessages}
            title='Agent Progress'
            isComplete={taskStatus === AgentTaskStatus.REFACTOR_CODE_GENERATED}
          />

          {/* Documentation Dropdown - Always show */}
          <FileDropdown
            title='Documentation'
            icon='document'
            files={documentationDiffs}
            onFileClick={handleViewFiles}
            scrollAfterFiles={3}
            iconColor='#65558f'
            titleColor='#65558f'
          />

          {/* Code Dropdown - Always show */}
          <FileDropdown
            title='Code'
            icon='code'
            files={codeDiffs}
            onFileClick={handleViewFiles}
            scrollAfterFiles={3}
            iconColor='#65558f'
            titleColor='#65558f'
          />
        </div>
      </div>
    </div>
  )
}
